/**
 * Informant input (ADHD-21, ADHD-24): ask someone who knows the client well for
 * their view, follow the request, and compare their answers with the client's.
 */
import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import api from '../../services/api';

const RELATIONSHIPS = { partner: 'Partner', parent: 'Parent', sibling: 'Brother or sister', friend: 'Friend', other: 'Other' };
const STATUS = {
  pending: ['Sent', 'default'], opened: ['Opened', 'info'], completed: ['Answered', 'success'],
  revoked: ['Withdrawn', 'default'], expired: ['Link expired', 'warning']
};
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
const fmt = (n) => (n === null || n === undefined ? '–' : Number(n).toFixed(1));

function InviteDialog({ open, onClose, clientId, onSent }) {
  const [form, setForm] = useState({ informantName: '', informantEmail: '', relationship: '', knewInChildhood: false, clientAgreed: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const ready = form.informantName && form.informantEmail && form.relationship && form.clientAgreed;

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      await api.post(`/informants/client/${clientId}/requests`, form);
      onSent();
      onClose();
      setForm({ informantName: '', informantEmail: '', relationship: '', knewInChildhood: false, clientAgreed: false });
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send the request.');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Ask someone who knows the client</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          They get a private link by email to a short questionnaire about what they have noticed, now and, if they knew the client then, in childhood. No account is needed.
        </Typography>
        <TextField fullWidth label="Their name" value={form.informantName} onChange={set('informantName')} sx={{ mb: 2 }} />
        <TextField fullWidth label="Their email" type="email" value={form.informantEmail} onChange={set('informantEmail')} sx={{ mb: 2 }} />
        <FormControl fullWidth sx={{ mb: 1 }}>
          <InputLabel id="rel-label">Relationship to the client</InputLabel>
          <Select labelId="rel-label" label="Relationship to the client" value={form.relationship} onChange={set('relationship')}>
            {Object.entries(RELATIONSHIPS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControlLabel control={<Checkbox checked={form.knewInChildhood} onChange={set('knewInChildhood')} />} label="They knew the client as a child" />
        <FormControlLabel control={<Checkbox checked={form.clientAgreed} onChange={set('clientAgreed')} />}
          label="The client has agreed that we contact this person" />
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!ready || busy} onClick={submit}>{busy ? 'Sending…' : 'Send request'}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function InformantPanel({ clientId }) {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!clientId) return;
    try {
      const res = await api.get(`/informants/client/${clientId}`);
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 403) setHidden(true);
      else setError('Could not load informant input.');
    }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    try { await api.post(`/informants/requests/${id}/${action}`); load(); }
    catch (err) { setError(err.response?.data?.error || 'That did not work.'); }
  };

  if (hidden || !data || data.enabled === false) return null;
  const requests = data.requests || [];
  const results = data.results || [];
  const self = data.selfAsrs;
  // Only relevant on an ADHD pathway: show once the client has an ASRS or any informant request.
  if (!self && !requests.length && !results.length) return null;
  const area = (r, id) => r.resultDetails?.subscaleScores?.find(s => s.id === id)?.score;
  const freeText = (r) => (r.answers || []).filter(a => /_(now_examples|child_examples|anything_else)$/.test(a.questionId) && a.answerValue);

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="informant-panel">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Informant input</Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="outlined" onClick={() => setOpen(true)}>Ask someone who knows the client</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Views from a partner, parent or friend, for the diagnosing clinician. The questionnaire is a draft and has no cut-off scores.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      {requests.map(r => {
        const [label, colour] = STATUS[r.status] || [r.status, 'default'];
        return (
          <Box key={r._id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, borderTop: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>{r.informantName}</Typography>
            <Typography variant="body2" color="text.secondary">{RELATIONSHIPS[r.relationship]} · asked {fmtDate(r.createdAt)}</Typography>
            <Chip size="small" label={label} color={colour} variant="outlined" />
            {r.emailError && <Chip size="small" color="error" label="Email failed" variant="outlined" />}
            <Box sx={{ flex: 1 }} />
            {['pending', 'opened', 'expired'].includes(r.status) && <Button size="small" onClick={() => act(r._id, 'resend')}>Send a new link</Button>}
            {['pending', 'opened'].includes(r.status) && <Button size="small" color="inherit" onClick={() => act(r._id, 'revoke')}>Withdraw</Button>}
          </Box>
        );
      })}

      {results.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Client and informant side by side</Typography>
          <Typography variant="caption" color="text.secondary">
            Average ratings, 0 (never) to 4 (very often). The client's figures come from the ASRS and the informant's from a different questionnaire, so compare the direction, not the exact numbers.
          </Typography>
          <Table size="small" sx={{ mt: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell>Area</TableCell>
                <TableCell>Client (ASRS{self ? `, ${fmtDate(self.submittedAt)}` : ''})</TableCell>
                {results.map(r => <TableCell key={r._id}>{r.respondent?.name} ({RELATIONSHIPS[r.respondent?.relationship] || 'informant'})</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow><TableCell>Now: attention and organisation</TableCell><TableCell>{fmt(self?.areas?.attention)}</TableCell>{results.map(r => <TableCell key={r._id}>{fmt(area(r, 'now_attention'))}</TableCell>)}</TableRow>
              <TableRow><TableCell>Now: restlessness and impulsivity</TableCell><TableCell>{fmt(self?.areas?.activity)}</TableCell>{results.map(r => <TableCell key={r._id}>{fmt(area(r, 'now_activity'))}</TableCell>)}</TableRow>
              <TableRow><TableCell>Childhood: attention</TableCell><TableCell>–</TableCell>{results.map(r => <TableCell key={r._id}>{fmt(area(r, 'child_attention'))}</TableCell>)}</TableRow>
              <TableRow><TableCell>Childhood: restlessness and impulsivity</TableCell><TableCell>–</TableCell>{results.map(r => <TableCell key={r._id}>{fmt(area(r, 'child_activity'))}</TableCell>)}</TableRow>
            </TableBody>
          </Table>
          {results.map(r => freeText(r).length > 0 && (
            <Box key={r._id} sx={{ mt: 1.5 }}>
              <Typography variant="subtitle2">In {r.respondent?.name}'s words</Typography>
              {freeText(r).map(a => (
                <Typography key={a.questionId} variant="body2" sx={{ mt: 0.5 }}><em>{a.questionText}</em> {a.answerValue}</Typography>
              ))}
            </Box>
          ))}
        </Box>
      )}
      <InviteDialog open={open} onClose={() => setOpen(false)} clientId={clientId} onSent={load} />
    </Paper>
  );
}
